/**
 * match-renderer.ts - Match Rendering Functions
 *
 * Handles rendering matches, rounds, and match tables
 * for Swiss, Round-Robin, and Knockout tournaments.
 */

import type { Match, Tournament } from '../../../types/api';
import { API_BASE_URL, TOURNAMENT_FORMATS, SPECIAL_PLAYER_IDS } from '../../utils/constants';
import { t } from '../../utils/i18n-helper';
import { escapeHtml } from '../../utils/security';
import { calculatePlayerScoresBeforeRound } from '../../utils/score-calculator';
import { buildRoundSelector, getKnockoutRoundName } from './round-selector';
import { deleteMatch } from '../../api/tournament-api';
import { showConfirmDialog } from '../../utils/confirm-dialog';

/**
 * Check if we're on the public share page (read-only view)
 */
function isPublicView(): boolean {
  // Check if URL contains /share/ or if the container has .read-only class
  return window.location.pathname.includes('/share/') ||
         document.querySelector('.container.read-only') !== null;
}

/**
 * Render matches for a tournament
 * @param matches - Array of match objects
 * @param tournamentId - The tournament ID
 * @param tournamentFormat - The tournament format
 * @param displayRound - The round to display
 * @param playerCount - Number of players in tournament
 * @param maxRounds - Maximum number of rounds
 * @param tournament - Optional full tournament object for permission checks
 * @returns HTML string for the matches
 */
export function renderMatches(
  matches: Match[],
  tournamentId: string,
  tournamentFormat: string,
  displayRound: number | null = null,
  playerCount: number = 0,
  maxRounds: number | null = null,
  tournament?: Tournament
): string {
  if (!matches || matches.length === 0) {
    // For tournaments with no matches, show Round 1 button
    if (tournamentFormat === TOURNAMENT_FORMATS.SWISS || tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN || tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT) {
      if (tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN) {
        return `
          <div class="round-selector">
            <button class="btn-small" style="background-color: #6c757d; color: white; border: 1px solid #6c757d;" onclick="generateRoundRobinPairings('${tournamentId}', false)">${t('pairings.singleRoundRobin', 'Single Round-Robin')}</button>
            <button class="btn-small" style="background-color: #6c757d; color: white; border: 1px solid #6c757d; margin-left: 8px;" onclick="generateRoundRobinPairings('${tournamentId}', true)">${t('pairings.doubleRoundRobin', 'Double Round-Robin')}</button>
          </div>
          <p>${t('pairings.chooseFormat', 'No matches yet. Choose single or double round-robin format.')}</p>
        `;
      }

      // Check if Swiss tournament has enough players for the number of rounds
      // Minimum players = numberOfRounds + 1
      if (tournamentFormat === TOURNAMENT_FORMATS.SWISS && maxRounds) {
        const minimumPlayersNeeded = maxRounds + 1;
        if (playerCount < minimumPlayersNeeded) {
          return `
            <div class="round-selector">
              <p style="margin: 0;">${t('pairings.noPairings', 'No matches yet.')}</p>
            </div>
            <div class="not-enough-players-warning" style="margin-top: 8px; padding: 8px 12px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404; font-size: 0.9em;">
              ⚠️ ${t('pairings.notEnoughPlayers', 'Not enough players for {rounds} rounds. Minimum {min} players required, currently {current}.')
                .replace('{rounds}', String(maxRounds))
                .replace('{min}', String(minimumPlayersNeeded))
                .replace('{current}', String(playerCount))}
              <br><span style="font-size: 0.9em; color: #6c757d;">${t('pairings.notEnoughPlayersHint', 'Either reduce the number of rounds in tournament settings, or add more players.')}</span>
            </div>
          `;
        }
      }

      const buttonText = `+ ${t('round.round', 'Round')} 1`;
      return `
        <div class="round-selector">
          <button id="generate-pairings-${tournamentId}" class="btn-small" style="background-color: #6c757d; color: white; border: 1px solid #6c757d;" onclick="generatePairings('${tournamentId}')">${buttonText}</button>
        </div>
        <p>${t('pairings.generateToBegin', 'No matches yet. Generate pairings to begin.')}</p>
      `;
    }
    return `<p>${t('pairings.noPairings', 'No matches yet.')}</p>`;
  }

  // Group matches by round
  const matchesByRound: Record<number, Match[]> = {};
  matches.forEach(match => {
    if (!matchesByRound[match.round]) {
      matchesByRound[match.round] = [];
    }
    matchesByRound[match.round]!.push(match);
  });

  // Find the current (highest) round
  const rounds = Object.keys(matchesByRound).map(r => parseInt(r, 10)).sort((a, b) => a - b);
  // For round-robin, default to round 1 on initial load; for other formats, default to latest round
  const defaultRound = tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN
    ? (rounds[0] ?? 1)
    : (rounds[rounds.length - 1] ?? 1);
  const currentRound = displayRound !== null ? displayRound : defaultRound;

  // If only one round, show it directly without tabs
  if (rounds.length === 1) {
    // Check if all results are entered for the single round
    const allResultsEntered = matchesByRound[currentRound]!.every(m => {
      const isBye = m.isBye || m.player2?.id === SPECIAL_PLAYER_IDS.BYE;
      const hasResult = !!(m.result && m.result !== '' && m.result !== 'null');
      return isBye || hasResult;
    });

    // For knockout, calculate expected total rounds based on player count
    const expectedTotalRounds = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT && playerCount > 0
      ? Math.ceil(Math.log2(playerCount))
      : rounds.length;

    let html = renderSingleRoundMatches(matchesByRound[currentRound]!, currentRound, true, matches, tournamentFormat, expectedTotalRounds, tournament);

    // Build round selector with single round button
    const currentRoundName = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT ? getKnockoutRoundName(currentRound, expectedTotalRounds) : `${t('round.round', 'Round')} ${currentRound}`;
    const roundButtons = [`<button class="btn btn-small btn-primary" data-round="${currentRound}">${currentRoundName}</button>`];

    const roundSelector = buildRoundSelector({
      tournamentId,
      tournamentFormat,
      roundButtons,
      currentRound,
      latestRound: currentRound,
      totalRounds: expectedTotalRounds,
      allResultsEntered,
      maxRounds,
      playerCount
    });

    return roundSelector + html;
  }

  // Multiple rounds - create round selector tabs
  const latestRound = Math.max(...rounds);
  // For knockout, calculate expected total rounds based on player count
  // For other formats, use the number of rounds generated so far
  const totalRounds = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT && playerCount > 0
    ? Math.ceil(Math.log2(playerCount))
    : rounds.length;

  // Check if all results from the latest round are entered (excluding byes)
  const allResultsEntered = matchesByRound[latestRound]!.every(m => {
    const isBye = m.isBye || m.player2?.id === SPECIAL_PLAYER_IDS.BYE;
    const hasResult = !!(m.result && m.result !== '' && m.result !== 'null');
    return isBye || hasResult;
  });

  const roundButtons = rounds.map(round => {
    const roundName = tournamentFormat === TOURNAMENT_FORMATS.KNOCKOUT ? getKnockoutRoundName(round, totalRounds) : `${t('round.round', 'Round')} ${round}`;
    return `<button class="btn btn-small ${round === currentRound ? 'btn-primary' : 'btn-secondary'}" data-round="${round}" onclick="showMatchRound('${tournamentId}', ${round})">${roundName}</button>`;
  });

  // Build round selector using shared helper function
  let html = buildRoundSelector({
    tournamentId,
    tournamentFormat,
    roundButtons,
    currentRound,
    latestRound,
    totalRounds,
    allResultsEntered,
    maxRounds,
    playerCount
  });

  // Add containers for each round
  html += '<div class="rounds-container">';
  rounds.forEach(round => {
    const isCurrentRound = round === currentRound;
    html += `<div id="match-round-${tournamentId}-${round}" class="round-content" style="display: ${round === currentRound ? 'block' : 'none'};">`;
    html += renderSingleRoundMatches(matchesByRound[round]!, round, isCurrentRound, matches, tournamentFormat, totalRounds, tournament);
    html += '</div>';
  });
  html += '</div>';

  return html;
}

/**
 * Render matches for a single round
 * @param roundMatches - Matches for the round
 * @param round - The round number
 * @param isCurrentRound - Whether this is the current round
 * @param allMatches - All matches in the tournament
 * @param tournamentFormat - The tournament format
 * @param totalRounds - Total number of rounds
 * @param tournament - Optional full tournament object for permission checks
 * @returns HTML string for the round matches
 */
function renderSingleRoundMatches(
  roundMatches: Match[],
  round: number,
  isCurrentRound: boolean,
  allMatches: Match[] = [],
  tournamentFormat: string = TOURNAMENT_FORMATS.SWISS,
  _totalRounds: number = 1,
  tournament?: Tournament
): string {
  // Get current user
  const currentUser = window.getCurrentUser ? window.getCurrentUser() : null;
  const currentUserId = currentUser?.id;

  // Debug: Log auth state for troubleshooting (can be removed later)
  if (tournament && tournament.userId) {
    console.log('[MatchRenderer] Auth check:', {
      tournamentId: tournament.id,
      tournamentUserId: tournament.userId,
      currentUserId: currentUserId,
      isOwner: currentUserId && String(tournament.userId) === String(currentUserId),
      isAdmin: currentUser?.role === 'admin',
      getCurrentUserExists: !!window.getCurrentUser,
      currentUserObj: currentUser ? { id: currentUser.id, username: currentUser.username, role: currentUser.role } : null
    });
  }

  // Check if user can edit results for this tournament/match
  const canEditResults = (match: Match): boolean => {
    // If no tournament object, allow editing (backward compatibility)
    if (!tournament) return true;

    // Unauthenticated tournaments (no userId) can be edited by anyone
    if (!tournament.userId) return true;

    // If getCurrentUser is not available, allow editing (backward compatibility for owner view)
    if (!window.getCurrentUser) return true;

    // Tournament owner can always edit (even completed results for override)
    const isOwner = currentUserId && String(tournament.userId) === String(currentUserId);
    if (isOwner) return true;

    // Admins can always edit any tournament
    const isAdmin = currentUser?.role === 'admin';
    if (isAdmin) return true;

    // If user result entry is disabled, non-owners cannot edit
    if (!tournament.allowUserResultEntry) return false;

    // If not authenticated, cannot edit
    if (!currentUserId) return false;

    // Check if user is one of the players in this match
    const userPlayer = tournament.players?.find(p => p.userId === currentUserId);
    if (!userPlayer) return false;

    // Check if this specific player is allowed to enter results
    if (userPlayer.canEnterResults === false) return false;

    // Check if this player is in this specific match
    const isPlayerInMatch = match.player1?.id === userPlayer.id || match.player2?.id === userPlayer.id;
    if (!isPlayerInMatch) return false;

    // Players can only edit current round results (except in round robin where they can edit any round)
    if (tournamentFormat !== TOURNAMENT_FORMATS.ROUND_ROBIN && round !== tournament.currentRound) return false;

    // In round robin, players can edit existing results to allow corrections
    // In other formats, players cannot edit if result already exists
    if (match.result && tournamentFormat !== TOURNAMENT_FORMATS.ROUND_ROBIN) return false;

    return true;
  };

  // Calculate scores before this round (still needed for display)
  const scoresBeforeRound = calculatePlayerScoresBeforeRound(allMatches, round);

  // For Round Robin, preserve the original board order. For other formats, sort by ranking.
  const sortedMatches = [...roundMatches].sort((a, b) => {
    // Always put BYE matches at the end
    const player2IdA = a.player2?.id || null;
    const player2IdB = b.player2?.id || null;
    const isByeA = a.isBye || player2IdA === SPECIAL_PLAYER_IDS.BYE;
    const isByeB = b.isBye || player2IdB === SPECIAL_PLAYER_IDS.BYE;

    if (isByeA && !isByeB) return 1;  // a is BYE, move to end
    if (!isByeA && isByeB) return -1; // b is BYE, move to end
    if (isByeA && isByeB) return 0;   // both are BYE, keep order

    // For Round Robin: Use board number to preserve original pairing order
    if (tournamentFormat === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      const boardA = a.boardNumber ?? 999999;
      const boardB = b.boardNumber ?? 999999;
      return boardA - boardB;
    }

    // For Swiss/Knockout: Sort by the highest-ranked (best) player in each pairing
    // This ensures board 1 has the top player, regardless of color

    // Get scores for both players in each match
    const score1A = scoresBeforeRound[a.player1?.id || ''] || 0;
    const score2A = scoresBeforeRound[a.player2?.id || ''] || 0;
    const score1B = scoresBeforeRound[b.player1?.id || ''] || 0;
    const score2B = scoresBeforeRound[b.player2?.id || ''] || 0;

    // Primary: Sort by highest score in the pairing (descending)
    const maxScoreA = Math.max(score1A, score2A);
    const maxScoreB = Math.max(score1B, score2B);
    if (maxScoreA !== maxScoreB) {
      return maxScoreB - maxScoreA;
    }

    // Secondary: Sort by highest rating in the pairing (descending)
    const rating1A = a.player1?.rating || 0;
    const rating2A = a.player2?.rating || 0;
    const rating1B = b.player1?.rating || 0;
    const rating2B = b.player2?.rating || 0;
    const maxRatingA = Math.max(rating1A, rating2A);
    const maxRatingB = Math.max(rating1B, rating2B);
    if (maxRatingA !== maxRatingB) {
      return maxRatingB - maxRatingA;
    }

    // Tertiary: Sort by best (lowest) initial ranking in the pairing
    const tpn1A = a.player1?.initialRanking || 999999;
    const tpn2A = a.player2?.initialRanking || 999999;
    const tpn1B = b.player1?.initialRanking || 999999;
    const tpn2B = b.player2?.initialRanking || 999999;
    const minTpnA = Math.min(tpn1A, tpn2A);
    const minTpnB = Math.min(tpn1B, tpn2B);
    return minTpnA - minTpnB;
  });

  // For Swiss format, calculate unpaired players
  const isSwiss = tournamentFormat === TOURNAMENT_FORMATS.SWISS;
  const tournamentId = tournament?.id || '';
  let hasUnpairedPlayers = false;

  // Check if this is a public/share view (no editing allowed)
  const isReadOnly = isPublicView();

  if (isSwiss && tournament?.players) {
    // Get all player IDs that are paired in this round
    const pairedPlayerIds = new Set<string>();
    roundMatches.forEach(match => {
      if (match.player1?.id) pairedPlayerIds.add(match.player1.id);
      if (match.player2?.id && match.player2.id !== SPECIAL_PLAYER_IDS.BYE) {
        pairedPlayerIds.add(match.player2.id);
      }
    });
    // Check if any players are not paired
    hasUnpairedPlayers = tournament.players.some(p => !pairedPlayerIds.has(p.id));
  }

  // Don't show edit controls in public view
  const showEditControls = isSwiss && !isReadOnly;

  return `
    <div class="round-section">
      <table class="matches-table">
        <thead>
          <tr>
            <th style="width: 30px;">#</th>
            <th>${t('pairings.white', 'White')}</th>
            <th>${t('pairings.black', 'Black')}</th>
            <th>${t('pairings.result', 'Result')}</th>
            ${showEditControls ? `<th style="width: 40px;"></th>` : ''}
          </tr>
        </thead>
        <tbody>
          ${sortedMatches.map((match, index) => {
            const player1 = match.player1 || {};
            const player2 = match.player2 || {};

            const player1Id = player1.id || null;
            const player2Id = player2.id || null;

            const player1Name = `${player1.firstName ? escapeHtml(player1.firstName) : ''} ${player1.lastName ? escapeHtml(player1.lastName) : ''}`.trim() || 'TBD';
            const player2NameText = `${player2.firstName ? escapeHtml(player2.firstName) : ''} ${player2.lastName ? escapeHtml(player2.lastName) : ''}`.trim();

            const isBye = match.isBye || player2Id === SPECIAL_PLAYER_IDS.BYE;
            const opponentPending = !isBye && !player2Id;
            const hasResult = match.result && match.result !== '';

            const player1Title = player1.title ? `${escapeHtml(player1.title)} ` : '';
            const player2Title = player2.title && !isBye && player2Id ? `${escapeHtml(player2.title)} ` : '';
            const player1Rating = player1.rating ? ` <span style="color: #666; font-size: 0.9em;">(${player1.rating})</span>` : '';
            const player2Rating = player2.rating && !isBye && player2Id ? ` <span style="color: #666; font-size: 0.9em;">(${player2.rating})</span>` : '';
            const player1Federation = player1.federation ? ` <span style="color: #666; font-size: 0.9em;">${escapeHtml(player1.federation)}</span>` : '';
            const player2Federation = player2.federation && !isBye && player2Id ? ` <span style="color: #666; font-size: 0.9em;">${escapeHtml(player2.federation)}</span>` : '';

            const player1Score = player1Id ? (scoresBeforeRound[player1Id] || 0) : null;
            const player2Score = player2Id && !isBye ? (scoresBeforeRound[player2Id] || 0) : null;

            const renderScore = (score: number | null): string => {
              if (score === null || score === undefined) return '';
              const normalized = score % 1 === 0 ? score.toString() : score.toFixed(1);
              return ` <span style="color: #666; font-size: 0.9em;">[${normalized}]</span>`;
            };

            const player1Display = `${player1Title}${player1Name}${player1Rating}${player1Federation}${renderScore(player1Score)}`;
            const player2Display = isBye
              ? t('pairings.bye', SPECIAL_PLAYER_IDS.BYE)
              : opponentPending
                ? `<em>${t('pairings.awaitingOpponent', 'Awaiting opponent')}</em>`
                : `${player2Title}${(player2NameText || 'TBD')}${player2Rating}${player2Federation}${renderScore(player2Score)}`;

            // Add click handler for rows with selectable results
            const isClickable = !isBye && !opponentPending && canEditResults(match);
            const cellClickHandler = isClickable ? ` onclick="setActiveMatchByClick('${match.id}', '${tournamentId}')" style="cursor: pointer;"` : '';

            return `
            <tr>
              <td${cellClickHandler} style="width: 30px; font-weight: 600;">${index + 1}</td>
              <td${cellClickHandler}>${player1Display}</td>
              <td${cellClickHandler}>${player2Display}</td>
              <td class="result-cell" data-match-id="${match.id}">
                ${isBye ? `${match.result || '1-0'} (${t('pairings.bye', 'Bye')})` : opponentPending ? `<em>${t('pairings.pending', 'Pending')}</em>` : (!isBye && canEditResults(match) ? `
                  <select id="result-${match.id}" class="result-select" data-round="${round}" data-previous-value="${match.result || ''}" tabindex="-1" onchange="submitResult('${match.id}'); this.blur();">
                    <option value="">${t('pairings.select', 'Select...')}</option>
                    <option value="1-0" ${match.result === '1-0' ? 'selected' : ''}>1-0 (${t('pairings.whiteWins', 'White wins')})</option>
                    ${tournamentFormat !== 'knockout' ? `<option value="0.5-0.5" ${match.result === '0.5-0.5' ? 'selected' : ''}>½-½ (${t('pairings.draw', 'Draw')})</option>` : ''}
                    <option value="0-1" ${match.result === '0-1' ? 'selected' : ''}>0-1 (${t('pairings.blackWins', 'Black wins')})</option>
                    <option value="1-0ff" ${match.result === '1-0ff' ? 'selected' : ''}>1-0 FF (${t('pairings.blackForfeit', 'Black forfeit')})</option>
                    <option value="0-1ff" ${match.result === '0-1ff' ? 'selected' : ''}>0-1 FF (${t('pairings.whiteForfeit', 'White forfeit')})</option>
                    ${tournamentFormat !== 'knockout' ? `<option value="0-0" ${match.result === '0-0' ? 'selected' : ''}>0-0 (${t('pairings.doubleForfeit', 'Both no-show')})</option>` : ''}
                  </select>
                ` : hasResult ? match.result : `<em>${t('pairings.pending', 'Pending')}</em>`)}
              </td>
              ${showEditControls ? `
              <td class="delete-match-cell">
                <button class="btn-icon btn-delete-match" onclick="deleteMatchFromRound('${tournamentId}', '${match.id}')" title="${t('pairings.deleteMatch', 'Delete pairing')}">🗑</button>
              </td>
              ` : ''}
            </tr>
          `}).join('')}
          ${showEditControls && tournamentId && hasUnpairedPlayers ? `
          <tr class="manual-pairing-row">
            <td colspan="5">
              <button class="btn-small btn-secondary manual-pairing-btn" onclick="showManualPairingModal('${tournamentId}', ${round})">
                ➕ ${t('pairings.manualPairing', 'Manual pairing')}
              </button>
            </td>
          </tr>
          ` : ''}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Get the currently viewed round for a tournament
 * @param tournamentId - The tournament ID
 * @returns The currently viewed round number or null
 */
export function getCurrentlyViewedRound(tournamentId: string): number | null {
  // Method 1: Check which round div is visible
  let currentlyViewedRound: number | null = null;
  const allRounds = document.querySelectorAll(`[id^="match-round-${tournamentId}-"]`);
  allRounds.forEach(el => {
    const htmlEl = el as HTMLElement;
    if (htmlEl.style.display !== 'none' && htmlEl.style.display !== '') {
      // Extract round number from ID format: match-round-{tournamentId}-{round}
      // Split by the known prefix and get the last part
      const prefix = `match-round-${tournamentId}-`;
      if (htmlEl.id.startsWith(prefix)) {
        const roundStr = htmlEl.id.substring(prefix.length);
        const roundNum = parseInt(roundStr, 10);
        if (!isNaN(roundNum)) {
          currentlyViewedRound = roundNum;
        }
      }
    }
  });

  // Method 2: If that didn't work, check which round button is active
  if (currentlyViewedRound === null) {
    const roundSelector = document.querySelector(`.round-selector [onclick*="'${tournamentId}'"]`)?.closest('.round-selector');
    if (roundSelector) {
      const activeBtn = roundSelector.querySelector('.btn-primary[onclick^="showMatchRound"]');
      if (activeBtn) {
        const onclick = activeBtn.getAttribute('onclick');
        const match = onclick?.match(/showMatchRound\('([^']+)',\s*(\d+)\)/);
        if (match && match[2]) {
          currentlyViewedRound = parseInt(match[2]);
        }
      }
    }
  }

  return currentlyViewedRound;
}

/**
 * Refresh tournament pairings display
 * @param tournamentId - The tournament ID
 * @param skipKeyboardReinit - Whether to skip re-initializing keyboard shortcuts (used during result entry)
 */
export async function refreshTournamentPairings(tournamentId: string, skipKeyboardReinit = false): Promise<void> {
  try {
    // Remember which round is currently being viewed
    const currentlyViewedRound = getCurrentlyViewedRound(tournamentId);

    // Fetch updated tournament data
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      console.error('Failed to fetch tournament data');
      return;
    }

    const tournament: Tournament = await response.json();

    // Defensive: ensure players and matches arrays exist
    if (!tournament.players) {
      console.error('[refreshTournamentPairings] Tournament missing players array:', tournament);
      tournament.players = [];
    }
    if (!tournament.matches) {
      console.error('[refreshTournamentPairings] Tournament missing matches array:', tournament);
      tournament.matches = [];
    }

    // Update the pairings tab content
    const pairingsDiv = document.getElementById(`pairings-tab-${tournamentId}`);
    if (pairingsDiv) {
      // Store scroll position
      const scrollTop = pairingsDiv.scrollTop;

      // Pass the currentlyViewedRound to renderMatches so it renders with that round visible
      pairingsDiv.innerHTML = renderMatches(tournament.matches, tournamentId, tournament.format, currentlyViewedRound, tournament.players.length, tournament.numberOfRounds, tournament);

      // Restore scroll position
      pairingsDiv.scrollTop = scrollTop;

      // Re-initialize keyboard shortcuts if the pairings tab is active
      // Skip this if we're in the middle of keyboard result entry (to preserve focus navigation)
      if (!skipKeyboardReinit && pairingsDiv.classList.contains('active')) {
        setTimeout(() => {
          if (window.initKeyboardShortcuts) {
            window.initKeyboardShortcuts(tournamentId);
          }
          // Re-apply the hide completed filter if it was active
          const hideCompletedCheckbox = document.getElementById(`hide-completed-${tournamentId}`) as HTMLInputElement;
          if (hideCompletedCheckbox && hideCompletedCheckbox.checked && window.toggleCompletedResults) {
            window.toggleCompletedResults(tournamentId, true);
          }
        }, 100);
      }
    }
  } catch (error) {
    console.error('Error refreshing tournament pairings:', error);
  }
}

/**
 * Delete a match from a round
 * @param tournamentId - The tournament ID
 * @param matchId - The match ID to delete
 */
export async function deleteMatchFromRound(tournamentId: string, matchId: string): Promise<void> {
  const confirmed = await showConfirmDialog(
    t('pairings.confirmDeleteMatch', 'Are you sure you want to delete this pairing?')
  );

  if (!confirmed) {
    return;
  }

  try {
    // Get current round before deletion
    const currentRound = getCurrentlyViewedRound(tournamentId);

    await deleteMatch(tournamentId, matchId);

    // Save active tab before reload so we stay on pairings
    sessionStorage.setItem(`active-tab-${tournamentId}`, 'pairings');

    // Reload the tournament to refresh pairings
    if (window.loadSingleTournament) {
      await window.loadSingleTournament(tournamentId);
    }

    // Show the same round after reload
    if (currentRound && window.showMatchRound) {
      setTimeout(() => {
        window.showMatchRound(tournamentId, currentRound);
      }, 50);
    }
  } catch (error) {
    console.error('Error deleting match:', error);
    alert(error instanceof Error ? error.message : t('pairings.errorDeletingMatch', 'Error deleting pairing'));
  }
}

// Export to window for onclick handlers

window.renderMatches = renderMatches;
window.refreshTournamentPairings = refreshTournamentPairings;
window.getCurrentlyViewedRound = getCurrentlyViewedRound;
window.deleteMatchFromRound = deleteMatchFromRound;
